import { TimeRangeKind } from '@/api/generated/model';
import { TIME_RANGE_LABELS } from '@/comps/component-library/reports/TimeRangeSelectPanel';
import { MULTIPLE_COLORS } from '@/constants/ui.constants';
import {
  CalendarClock,
  Globe,
  Signal,
  SignalHigh,
  SignalLow,
  SignalMedium,
  Users,
  type LucideIcon,
} from 'lucide-react';

/** Primary chart accent (org / selected segment / score bars). */
export const AIFI_CHART_PRIMARY_COLOR = MULTIPLE_COLORS[0]!;

/** Secondary chart accent (benchmarks, envelopes, below-median scores). */
export const AIFI_CHART_SECONDARY_COLOR = '#fafafa';

/** Dashed org reference line on the segment over-time chart, and its tooltip swatch. */
export const AIFI_ORG_LINE_COLOR = 'rgba(255, 255, 255, 0.45)';

function methodologyDimensionColor(index: number): string {
  return MULTIPLE_COLORS[index % MULTIPLE_COLORS.length]!;
}

/** Pie/doughnut-style fill for level and ladder bars. */
export function pieChartAccentFill(color: string): string {
  return `${color}10`;
}

/** Level-mix tooltip text hierarchy (image 1 reference). */
export const AIFI_LEVEL_MIX_TOOLTIP_LEVEL_COLOR = 'rgba(255, 255, 255, 0.55)';
export const AIFI_LEVEL_MIX_TOOLTIP_PERCENT_COLOR = 'rgba(255, 255, 255, 0.95)';
export const AIFI_LEVEL_MIX_TOOLTIP_COUNT_COLOR = 'rgba(255, 255, 255, 0.32)';

export const AIFI_LEVELS = ['Expert', 'Practitioner', 'Beginner', 'Novice'] as const;

export type AifiLevel = (typeof AIFI_LEVELS)[number];

export function aifiLevelFromLabel(label: string): AifiLevel | undefined {
  const normalized = label.trim().toLowerCase();
  return AIFI_LEVELS.find((level) => level.toLowerCase() === normalized);
}

/**
 * Column tracks shared by the proficiency ladder and the level-mix panel below it, so
 * the migration cards line up with the ladder's first column.
 */
export const AIFI_LEVEL_GRID_TEMPLATE_COLUMNS = `repeat(${AIFI_LEVELS.length}, minmax(0, 1fr))`;
/** Tracks the level-mix chart body spans, i.e. everything but the migration cards. */
export const AIFI_LEVEL_CHART_COLUMN_SPAN = AIFI_LEVELS.length - 1;

/**
 * Who each proficiency group is, phrased against the six methodology dimensions.
 * One short sentence so the four ladder columns stay even.
 */
export const AIFI_LEVEL_DESCRIPTIONS: Record<AifiLevel, string> = {
  Expert: 'AI champions who consistently give clear context, constraints, and direction to get strong results quickly.',
  Practitioner: 'Confident AI users who handle most tasks well, with occasional gaps in context or structure.',
  Beginner: 'Developing AI users who understand the task but often leave out useful context, detail, or guidance.',
  Novice:
    'Early-stage AI users who rely on simple prompts and usually need more back-and-forth to reach a useful result.',
};

/** Signal-strength icons, so the four group titles read as an ordered ladder. */
export const AIFI_LEVEL_ICONS: Record<AifiLevel, LucideIcon> = {
  Expert: Signal,
  Practitioner: SignalHigh,
  Beginner: SignalMedium,
  Novice: SignalLow,
};

/** Icons for the segment kinds, keyed by the noun the pages already pass around. */
export const SEGMENT_NOUN_ICONS: Record<string, LucideIcon> = {
  team: Users,
  market: Globe,
  tenure: CalendarClock,
};

/** Icon for a segment noun ("team", "market", "tenure"), falling back to the team icon. */
export function segmentNounIcon(segmentNoun: string): LucideIcon {
  return SEGMENT_NOUN_ICONS[segmentNoun] ?? Users;
}

export const AIFI_LEVEL_COLORS: Record<AifiLevel, string> = {
  Expert: MULTIPLE_COLORS[3],
  Practitioner: MULTIPLE_COLORS[5],
  Beginner: MULTIPLE_COLORS[2],
  Novice: MULTIPLE_COLORS[7],
};

/** Org score line, targets, and high-score accents (non-chart UI). */
export const AIFI_ORG_COLOR = AIFI_LEVEL_COLORS.Expert;
export const AIFI_TARGET_COLOR = AIFI_LEVEL_COLORS.Expert;

/** Negative deltas and low-score accents. */
export const AIFI_NEGATIVE_COLOR = AIFI_LEVEL_COLORS.Novice;

/** Positive delta text on dark backgrounds. */
export const AIFI_POSITIVE_TEXT_COLOR = '#92C070';

/** Selected segment line on over-time charts. */
export const AIFI_SEGMENT_LINE_COLOR = AIFI_CHART_PRIMARY_COLOR;

/** Below-median segment / benchmark accents. */
export const AIFI_BENCHMARK_SCORE_COLOR = AIFI_CHART_SECONDARY_COLOR;
export const METHODOLOGY_DIMENSIONS = [
  {
    key: 'task_clarity',
    label: 'Task clarity',
    shortLabel: 'Task clarity',
    description: 'Clarifies goal before asking for help',
    coaching: 'State the goal and the task clearly in the first prompt.',
    weight: 30,
    color: methodologyDimensionColor(0),
  },
  {
    key: 'context_provision',
    label: 'Context provision',
    shortLabel: 'Context',
    description: 'Provides relevant context for the task',
    coaching: 'Include role, constraints, and relevant data in the request.',
    weight: 25,
    color: methodologyDimensionColor(1),
  },
  {
    key: 'output_specification',
    label: 'Output specification',
    shortLabel: 'Output spec',
    description: 'Specifies format and structure needed',
    coaching: 'Say what format, length, or structure the answer should take.',
    weight: 20,
    color: methodologyDimensionColor(2),
  },
  {
    key: 'prompt_length',
    label: 'Prompt length',
    shortLabel: 'Length',
    description: 'Provides sufficient detail while staying concise and focused',
    coaching: 'Use enough detail in the prompt without unnecessary filler.',
    weight: 10,
    color: methodologyDimensionColor(3),
  },
  {
    key: 'conversation_efficiency',
    label: 'Conversation efficiency',
    shortLabel: 'Efficiency',
    description: 'Reaches a usable answer in few turns',
    coaching: 'Aim to resolve the task in fewer back-and-forth turns.',
    weight: 10,
    color: methodologyDimensionColor(4),
  },
  {
    key: 'constraint_specification',
    label: 'Constraint specification',
    shortLabel: 'Constraints',
    description: 'Specifies constraints and requirements',
    coaching: 'State boundaries or exclusions up front.',
    weight: 5,
    color: methodologyDimensionColor(5),
  },
] as const;

/** Grid order for the upskilling 2×2 level cards (top-left → bottom-right). */
export const UPSKILLING_LEVEL_GRID_ORDER = [
  'Novice',
  'Beginner',
  'Practitioner',
  'Expert',
] as const satisfies readonly AifiLevel[];

/** Source levels that can promote to the next tier. */
export const PROMOTION_SOURCE_LEVELS = ['Novice', 'Beginner', 'Practitioner'] as const satisfies readonly AifiLevel[];

export const PROMOTION_NEXT_LEVEL: Record<(typeof PROMOTION_SOURCE_LEVELS)[number], AifiLevel> = {
  Novice: 'Beginner',
  Beginner: 'Practitioner',
  Practitioner: 'Expert',
};

export const DIMENSION_SCORE_CEILING = 100;

export const VS_PREVIOUS_LABEL: Partial<Record<TimeRangeKind, string>> = {
  [TimeRangeKind['7_days']]: 'vs. previous 7D',
  [TimeRangeKind['30_days']]: 'vs. previous 30D',
  [TimeRangeKind['3_months']]: 'vs. previous 3M',
  [TimeRangeKind['6_months']]: 'vs. previous 6M',
  [TimeRangeKind['12_months']]: 'vs. previous 12M',
};

/** Short bucket label for level-mix migration copy (e.g. "30D"). */
export function getAifiTimeRangeShortLabel(kind: TimeRangeKind): string {
  return TIME_RANGE_LABELS[kind] ?? '30D';
}

/** Trend chip suffix (e.g. "vs 30D"). */
export function getAifiTrendVsLabel(kind: TimeRangeKind): string {
  return `vs ${getAifiTimeRangeShortLabel(kind)}`;
}

/** Left gutter to align level-mix x-axis with the over-time plot area (y-axis labels). */
export const AIFI_CHART_Y_AXIS_GUTTER = 36;

/** Disable Chart.js mount animations (lines otherwise grow from the origin). */
export const AIFI_CHART_WITHOUT_ANIMATION = {
  animation: false,
} as const;

/**
 * Time-series / level-mix charts: no mount animation, but animate hover point radius/colors
 * like report line charts (`commonOptions.animation.duration` + default `transitions.active`).
 */
export const AIFI_LINE_CHART_OPTIONS = {
  animation: { duration: 0 },
  transitions: {
    active: {
      animation: {
        duration: 200,
      },
    },
    resize: {
      animation: {
        duration: 0,
      },
    },
  },
} as const;

export const SCORE_DESCRIPTION = 'How effectively users prompt and get tasks done with AI, scored 0 to 100.';

/** Size of the peer group the power-bar and trend benchmarks are drawn from. */
export const BENCHMARK_ORG_COUNT = 32;

export const BENCHMARK_MEDIAN_LABEL = 'Benchmark median';
export const BENCHMARK_TOP_QUARTILE_LABEL = 'Benchmark top quartile';

export const BENCHMARK_SOURCE_TEXT = `Benchmark is derived from ${BENCHMARK_ORG_COUNT} organizations`;

/**
 * Tooltip copy on the (i). Opens on the caption sentence so the two stay aligned,
 * then explains where the scores come from and what to compare against.
 */
export const BENCHMARK_DESCRIPTION =
  `${BENCHMARK_SOURCE_TEXT} that Nebuly monitors and scores the same way as yours. Compare your organization ` +
  'against their median and against the strongest quarter of them.';

/** Accessible name for the (i) link to the benchmark article. */
export const BENCHMARK_READ_MORE_LABEL = 'Read more about the benchmark';

// TODO: point at the published benchmark methodology article once it exists.
export const BENCHMARK_DOCS_URL = 'https://docs.nebuly.com/guides/ai-proficiency-benchmark';

/**
 * Tooltip copy on the group-progression cards. Movement is estimated by pairing the
 * headcount change of adjacent groups, so unmatched movement lands in `stayed`. The
 * period is interpolated so the copy tracks the range the cards are counting over.
 */
const LEVEL_MIGRATION_DESCRIPTIONS = {
  movedUp: (period: string) =>
    `Users who moved up at least one AI Proficiency group (Novice → Beginner → Practitioner → Expert) over the last ${period}.`,
  slippedBack: (period: string) =>
    `Users who moved down at least one AI Proficiency group (Expert → Practitioner → Beginner → Novice) over the last ${period}.`,
  stayed: (period: string) =>
    `Users who did not change AI Proficiency group over the last ${period}. New joiners, and movement that could not be matched between groups, also land here.`,
} as const;

export type LevelMigrationKey = keyof typeof LEVEL_MIGRATION_DESCRIPTIONS;

export function getLevelMigrationDescription(key: LevelMigrationKey, timeRangeLabel: string): string {
  return LEVEL_MIGRATION_DESCRIPTIONS[key](timeRangeLabel);
}
